import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/constants';

import {
  IMessageSearchingPayload,
  IResolveLinkWithDocumentPayload,
  IResolveLinkWithoutDocumentPayload,
  IRevertDocumentPayload,
} from '@/features/regulatory-intake/intake-details/interfaces';
import { IntakeDetailsService } from '@/features/regulatory-intake/intake-details/services';

export const useIntakeDetailsQuery = (id: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.regulatoryIntake.getIntakeDetails(id),
    queryFn: () => IntakeDetailsService.getIntakeDetails(id),
    placeholderData: keepPreviousData,
  });
};

export const useMessagesSearching = (payload: IMessageSearchingPayload) => {
  return useQuery({
    queryKey: QUERY_KEYS.regulatoryIntake.messagesSearch(payload),
    queryFn: () => IntakeDetailsService.getMessagesSearching(payload),
    placeholderData: keepPreviousData,
  });
};

export const useMessageReclassifyMutation = () => {
  return useMutation({
    mutationFn: async (emailId: string) => await IntakeDetailsService.reclassifyMessage(emailId),
  });
};

export const useJobRetryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string) => await IntakeDetailsService.jobRetry(jobId),
    onSuccess: (_, jobId) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.regulatoryIntake.getIntakeDetails(jobId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.regulatoryIntake.messagesSearch({}),
      });
    },
  });
};

export const useMessageResendMutation = () => {
  return useMutation({
    mutationFn: async (emailId: string) => await IntakeDetailsService.messageResend(emailId),
  });
};

export const useMessageResendAllMutation = (jobId: string) => {
  return useMutation({
    mutationFn: async () => await IntakeDetailsService.messageResendAll(jobId),
  });
};

export const useAttentionDocuments = (emailId: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.regulatoryIntake.getAttentionDocuments(emailId),
    queryFn: () => IntakeDetailsService.getAttentionDocuments(emailId),
    placeholderData: keepPreviousData,
  });
};

export const useResolveWithDocumentMutation = () => {
  return useMutation({
    mutationFn: async (params: IResolveLinkWithDocumentPayload) =>
      await IntakeDetailsService.resolveWithDocument(params),
  });
};

export const useResolveWithoutDocumentMutation = () => {
  return useMutation({
    mutationFn: async (params: IResolveLinkWithoutDocumentPayload) =>
      await IntakeDetailsService.resolveWithoutDocument(params),
  });
};

export const useTransferToN1Mutation = () => {
  return useMutation({
    mutationFn: async (emailId: string) => await IntakeDetailsService.transferToN1(emailId),
  });
};

export const useRevertDocumentMutation = () => {
  return useMutation({
    mutationFn: async (params: IRevertDocumentPayload) =>
      await IntakeDetailsService.revertDocument(params),
  });
};
